import type { LucideIcon } from "lucide-react";
import {
  Star,
  Download,
  GitFork,
  Loader2,
  Wallet,
  BarChart3,
  CalendarDays,
  HandCoins,
  SunMoon,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useGitHubStats } from "@/hooks/useGitHubStats";
import { trackPlayStoreClick, trackAppStoreClick, BADGE_LOCATIONS } from "@/lib/analytics";

// Store badges
import appStoreBadge from "@/assets/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg";
import playStoreBadge from "@/assets/images/GetItOnGooglePlay_Badge_Web_color_English.svg";

// Feature list with icons and unique colors
const features: { icon: LucideIcon; label: string; color: string }[] = [
  { icon: Wallet, label: "Track expenses", color: "text-emerald-500" },
  { icon: BarChart3, label: "Monthly reports", color: "text-blue-500" },
  { icon: CalendarDays, label: "Expense heatmap", color: "text-purple-500" },
  { icon: HandCoins, label: "Debts & debtors", color: "text-amber-500" },
  { icon: SunMoon, label: "Dark & light themes", color: "text-orange-500" },
  { icon: Download, label: "Export all data", color: "text-green-500" },
  { icon: RefreshCw, label: "Sync old data", color: "text-cyan-500" },
  { icon: ShieldCheck, label: "100% offline", color: "text-rose-500" },
];

export function FeaturedProduct() {
  // Fetch live GitHub stats for zero
  const gitHubStats = useGitHubStats("indranilbhuin", "zero");

  return (
    <div className="max-w-2xl mx-auto">

      <div className="rounded-xl border border-[--border-primary] bg-[--bg-secondary] p-6 md:p-8 relative overflow-hidden">
        {/* Subtle gradient accent */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-[--olive]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative">
          {/* Header */}
          <div className="flex items-center gap-2 md:gap-3 mb-4 flex-wrap">
            <Badge className="bg-amber-700 text-white hover:bg-amber-800 font-medium">
              Featured
            </Badge>
            <Badge variant="outline" className="border-[--olive]/30 text-[--olive]">
              Open Source
            </Badge>
            <Badge variant="outline" className="border-green-500/30 text-green-500">
              Live
            </Badge>
          </div>

          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-bold text-[--text-primary] mb-2">
            zero
          </h2>
          <p className="text-[--olive] text-base md:text-lg mb-4">
            embrace the simplicity of zero
          </p>

          {/* GitHub Stats - Live from API */}
          <div className="flex flex-wrap gap-3 mb-6">
            {gitHubStats.loading ? (
              <div className="flex items-center gap-1.5 text-sm text-[--text-muted]">
                <Loader2 className="size-4 animate-spin" />
                <span>Loading stats...</span>
              </div>
            ) : gitHubStats.error ? (
              <div className="flex items-center gap-1.5 text-sm text-[--text-muted]">
                <span>Stats unavailable</span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-1.5 text-sm text-[--text-secondary]">
                  <Star className="size-4 text-yellow-500" />
                  <span className="font-medium">{gitHubStats.stars}</span>
                  <span className="text-[--text-muted]">stars</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-[--text-secondary]">
                  <Download className="size-4 text-green-500" />
                  <span className="font-medium">{gitHubStats.downloads.toLocaleString()}</span>
                  <span className="text-[--text-muted]">downloads</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-[--text-secondary]">
                  <GitFork className="size-4 text-blue-500" />
                  <span className="font-medium">{gitHubStats.forks}</span>
                  <span className="text-[--text-muted]">forks</span>
                </div>
              </>
            )}
          </div>

          {/* Description */}
          <p className="text-[--text-secondary] mb-6 leading-relaxed text-sm md:text-base">
            A lightweight, privacy-first expense tracking app for Android & iOS. No servers, no data collection, 
            just your finances on your device. Track expenses, manage debts, view monthly reports with heatmaps.
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-2 md:gap-3 mb-6 text-xs md:text-sm">
            {features.map((feature) => (
              <div key={feature.label} className="flex items-center gap-2 text-[--text-muted]">
                <feature.icon className={`size-3.5 ${feature.color}`} />
                {feature.label}
              </div>
            ))}
          </div>

          {/* Store Badges & Learn More */}
          <div className="flex flex-wrap items-center gap-3">
            <a 
              href="https://play.google.com/store/apps/details?id=com.anotherwhy.zero" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              aria-label="Get it on Google Play"
              onClick={() => trackPlayStoreClick(BADGE_LOCATIONS.HOME)}
            >
              <img 
                src={playStoreBadge} 
                alt="Get it on Google Play" 
                height={40}
                className="h-10"
              />
            </a>
            <a 
              href="https://apps.apple.com/us/app/zero-offline-expense-tracker/id6759560225" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
              aria-label="Download on the App Store"
              onClick={() => trackAppStoreClick(BADGE_LOCATIONS.HOME)}
            >
              <img 
                src={appStoreBadge} 
                alt="Download on the App Store" 
                height={40}
                className="h-10"
              />
            </a>
            <Link 
              to="/products/awy-001"
              className="text-sm font-medium text-[--text-secondary] hover:text-[--olive] transition-colors ml-auto"
            >
              Learn more →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
